import { useCallback, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { CalendarBoardFacts, ItemLocalPresentation } from '../public';
import type { Mine } from './types';
import { useResource, useSpacesScope } from './resources';
const DEFAULT: ItemLocalPresentation = { boardIds: null, includeNoBoard: false, status: 'all' };
export function useBoardFacts(): CalendarBoardFacts | undefined {
    const scope = useSpacesScope();
    const query = useResource<Mine>(['spaces', 'boards-catalog'], '/v1/items/mine', { query: { limit: 1 }, enabled: scope.context.data?.capabilities.includes('items.v1') });
    const boards = query.data?.boards;
    return useMemo(() => boards ? ({ realmId: scope.realm, accountId: scope.account, generation: scope.runtime.generation,
        complete: boards.every(b => Array.isArray(b.board_item_ids)),
        boards: boards.map(b => ({ id: b.id, title: b.title, itemIds: b.board_item_ids ?? [] })) }) : undefined, [boards, scope.realm, scope.account, scope.runtime.generation]);
}
function validPreference(value: unknown): value is ItemLocalPresentation {
    if (!value || typeof value !== 'object')
        return false;
    const p = value as ItemLocalPresentation;
    return (p.boardIds === null || Array.isArray(p.boardIds) && p.boardIds.every(id => typeof id === 'string')) && typeof p.includeNoBoard === 'boolean' && (['all', 'open', 'done'].includes(p.status as string) || p.status !== null && typeof p.status === 'object' && typeof p.status.state === 'string');
}
export function usePresentation(): readonly [
    ItemLocalPresentation,
    (next: ItemLocalPresentation) => void
] {
    const { db } = useSpacesScope();
    const value = useLiveQuery(async () => (await db?.preferences.get('item-presentation'))?.value, [db]);
    const set = useCallback((next: ItemLocalPresentation) => {
        if (db && validPreference(next))
            void db.preferences.put({ key: 'item-presentation', value: next });
    }, [db]);
    return [validPreference(value) ? value : DEFAULT, set] as const;
}
